import { createSlice } from "@reduxjs/toolkit";

// Views & screens: twelve collections that all behave the same way.
//
// This used to be 62 hand-written reducers — five near-identical ones per
// collection, plus the initial state, repeated twelve times. The cost was not
// speed; it was that a fix applied to one collection silently missed the other
// eleven, and that the three places reactComponents genuinely differs were
// indistinguishable from copy-paste drift.
//
// One factory over a config table instead. Every deviation is now a declared
// flag rather than a difference you have to diff two hundred lines to find.
// The behavioural contract — including all three reactComponents deviations —
// is pinned by viewsSlice.test.js, which was written against the old
// hand-rolled version before this replaced it.

// The nine view/screen selections drive the same editor pane, so they are
// mutually exclusive: picking one clears the other eight. chartTemplates,
// chartScreens and mcScreens are independent and clear nothing.
const EXCLUSIVE_GROUP = [
  "RecordView", "DetailView", "ActivityView", "Screen", "DvScreen",
  "RvScreen", "AvScreen", "HeaderConfig", "ReactComponent",
];

const COLLECTIONS = [
  { plural: "recordViews",    single: "RecordView",    idKey: "rv_id" },
  { plural: "detailViews",    single: "DetailView",    idKey: "dv_id" },
  { plural: "activityViews",  single: "ActivityView",  idKey: "av_id" },
  { plural: "chartTemplates", single: "ChartTemplate", idKey: "ct_id" },
  { plural: "chartScreens",   single: "ChartScreen",   idKey: "id" },
  { plural: "mcScreens",      single: "McScreen",      idKey: "id" },
  { plural: "screens",        single: "Screen",        idKey: "id" },
  { plural: "dvScreens",      single: "DvScreen",      idKey: "id" },
  { plural: "rvScreens",      single: "RvScreen",      idKey: "id" },
  { plural: "avScreens",      single: "AvScreen",      idKey: "id" },
  { plural: "headerConfigs",  single: "HeaderConfig",  idKey: "id" },
  {
    plural: "reactComponents", single: "ReactComponent", idKey: "id",
    // reactComponents is the one collection that deviates, in three ways. These
    // are preserved deliberately, not by accident — callers rely on them.
    // Normalising any of them is a behaviour change that needs its own commit.
    replaceOnUpdate: true,                 // stores the payload as-is, no merge
    noEmptyDefault: true,                  // set(undefined) is NOT coerced to []
    deleteAction: "removeReactComponent",  // named remove*, not delete*InState
  },
];

const capitalize = (s) => s[0].toUpperCase() + s.slice(1);
const setAllName = (c) => `set${capitalize(c.plural)}`;
const deleteName = (c) => c.deleteAction || `delete${c.single}InState`;
const selectedKey = (single) => `selected${single}`;

// ─── initial state ────────────────────────────────────────────────────────────

const initialState = {
  formScreen: null,
  selectedFormDevice: "desktop",
};
for (const c of COLLECTIONS) {
  initialState[c.plural] = [];
  initialState[selectedKey(c.single)] = null;
}

// ─── reducers ─────────────────────────────────────────────────────────────────

const reducers = {
  setFormScreen: (state, action) => {
    state.formScreen = action.payload;
  },
  setSelectedFormDevice: (state, action) => {
    state.selectedFormDevice = action.payload;
  },
};

for (const c of COLLECTIONS) {
  const { plural, single, idKey } = c;
  const selected = selectedKey(single);
  const exclusive = EXCLUSIVE_GROUP.includes(single)
    ? EXCLUSIVE_GROUP.filter((other) => other !== single).map(selectedKey)
    : [];

  reducers[setAllName(c)] = (state, action) => {
    state[plural] = c.noEmptyDefault ? action.payload : action.payload || [];
  };

  reducers[`add${single}`] = (state, action) => {
    state[plural].push(action.payload);
  };

  reducers[`update${single}InState`] = (state, action) => {
    const updated = action.payload;
    const index = state[plural].findIndex((item) => item[idKey] === updated[idKey]);
    if (index !== -1) {
      state[plural][index] = c.replaceOnUpdate
        ? updated
        : { ...state[plural][index], ...updated };
    }
    // Keep the open editor's copy in step. Mirrored even when the entry is not
    // in the list, matching the original.
    if (state[selected] && state[selected][idKey] === updated[idKey]) {
      state[selected] = c.replaceOnUpdate ? updated : { ...state[selected], ...updated };
    }
  };

  reducers[deleteName(c)] = (state, action) => {
    const id = action.payload;
    state[plural] = state[plural].filter((item) => item[idKey] !== id);
    if (state[selected] && state[selected][idKey] === id) {
      state[selected] = null;
    }
  };

  reducers[`setSelected${single}`] = (state, action) => {
    state[selected] = action.payload;
    for (const other of exclusive) state[other] = null;
  };
}

// ─── slice ────────────────────────────────────────────────────────────────────

const viewsSlice = createSlice({
  name: "views",
  initialState,
  reducers,
  extraReducers: (builder) => {
    // Loading a different workflow invalidates every list and selection here.
    builder.addCase("workflow/resetWorkflow", (state) => {
      for (const c of COLLECTIONS) {
        state[c.plural] = [];
        state[selectedKey(c.single)] = null;
      }
      state.formScreen = null;
      state.selectedFormDevice = "desktop";
    });
  },
});

export default viewsSlice;
